import { ApolloError, NetworkStatus } from "@apollo/client"
import { Inventory, Inventory2, MoveToInbox } from "@mui/icons-material"
import CloudSyncIcon from "@mui/icons-material/CloudSync"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material"
import React, { useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  AuditLotStatus,
  FilterOperator,
  LogicalOperator,
  useGetAllAuditLotsQuery,
  useGetAuditProductSummaryQuery,
  useScanAuditLotMutation
} from "src/graphql/quark/quark.type"
import { Stack } from "src/shared/components"
import MinimulTabs from "src/shared/components/MinimalTabs/MinimulTabs"
import Switch from "src/shared/components/switch"
import BarcodeScanner from "src/shared/container/barcode-scanner"
import { useTypedSelector } from "src/shared/store/store"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import type { AuditProduct } from "../types"
import { validateBarcodeFormat, validateBarcodeFormatForBatch } from "./barcodeValidation"
import BatchQuantityDialog from "./BatchQuantityDialog"
import LotScanningSkeleton from "./LotScanningSkeleton"
import QuantityInputDialog from "./QuantityInputDialog"

interface LotScanningProps {
  product: AuditProduct
  onContinue: () => void
}

const LotScanning: React.FC<LotScanningProps> = ({ product, onContinue }) => {
  const [cardViewTab, setCardViewTab] = useState<"OPEN" | "BOX">("BOX")
  const [isBatchMode, setIsBatchMode] = useState<boolean>(false)
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false)
  const [batchQuantityDialogOpen, setBatchQuantityDialogOpen] = useState(false)
  const [scannedLotNumber, setScannedLotNumber] = useState("")
  const { id: auditId } = useParams()
  const currentWhLoc = useTypedSelector((state) => state.authentication.currentWhLoc)
  const [errorObject, setErrorObject] = useState<{
    message: string | null
    code: string | null
    variables: {
      quantity?: null | number
      barcode: string
      scanType?: "OPEN" | "BOX"
      openQuantity?: number | null
      boxQuantity?: number | null
    }
  } | null>(null)
  const [scanAuditLotMutation, { loading: isScanning }] = useScanAuditLotMutation({
    fetchPolicy: "network-only"
  })

  const {
    data: auditProductSummary,
    loading: isLoadingAuditProductSummary,
    refetch: refetchAuditProductSummary,
    networkStatus
  } = useGetAuditProductSummaryQuery({
    variables: {
      auditProductId: product.id as string
    },
    notifyOnNetworkStatusChange: true
  })

  // Separate query to fetch ALL lots for validation (no pagination)
  const { data: allAuditLotsData } = useGetAllAuditLotsQuery({
    fetchPolicy: "cache-and-network", // Use cache to avoid refetching
    variables: {
      limit: 10000, // High limit to get all lots
      page: 1,
      locationId: currentWhLoc?.id as string,
      filter: [
        {
          conditions: [
            {
              operator: FilterOperator.Equal,
              field: "auditProductId",
              value: product.id as string
            },
            {
              operator: FilterOperator.Ne,
              field: "status",
              value: AuditLotStatus.Matched
            }
          ],
          logicalOperator: LogicalOperator.And
        }
      ]
    }
  })

  // Get array of actionIds from all lots
  const actionIds = useMemo(() => {
    const allLots = allAuditLotsData?.getAllAuditLots.data || []
    return allLots.map((lot) => lot.actionId).filter((id): id is string => id !== null && id !== undefined)
  }, [allAuditLotsData])

  // Check if query is refetching
  const isRefetching = networkStatus === NetworkStatus.refetch

  console.log("errorObject", errorObject)

  const handleAddLot = async (barcode: string) => {
    if (!barcode.trim()) return
    setErrorObject(null)

    // If batch mode is enabled, validate and show batch quantity dialog
    if (isBatchMode) {
      // Validate barcode format for batch mode
      const validationResult = validateBarcodeFormatForBatch(barcode, actionIds)
      if (!validationResult.isValid) {
        toast.error("wrong lot number entered")
        return
      }
      // Use the corrected barcode (may be different from original if pipes were removed)
      setScannedLotNumber(validationResult.correctedBarcode)
      setBatchQuantityDialogOpen(true)
    } else {
      // Validate barcode format when batch mode is off
      if (!validateBarcodeFormat(barcode)) {
        toast.error("wrong lot number entered")
        return
      }

      // If cardViewTab is "OPEN", show quantity dialog
      if (cardViewTab === "OPEN") {
        setScannedLotNumber(barcode)
        setQuantityDialogOpen(true)
      } else {
        await handleOnScan({ barcode: barcode, scanType: "BOX" })
      }
    }
  }

  const handleOnScan = async ({
    barcode,
    quantity,
    scanType,
    openQuantity,
    boxQuantity,
    force = false
  }: {
    barcode: string
    quantity?: number | null
    scanType?: "OPEN" | "BOX"
    openQuantity?: number | null
    boxQuantity?: number | null
    force?: boolean
  }) => {
    try {
      // If batch mode, use provided openQuantity and boxQuantity
      // Otherwise, set openQuantity and boxQuantity based on scan type
      let finalOpenQuantity: number | null = null
      let finalBoxQuantity: number | null = null

      if (
        isBatchMode &&
        openQuantity !== undefined &&
        openQuantity !== null &&
        boxQuantity !== undefined &&
        boxQuantity !== null
      ) {
        finalOpenQuantity = openQuantity
        finalBoxQuantity = boxQuantity
      } else if (scanType) {
        finalOpenQuantity = scanType === "OPEN" ? (quantity ?? 0) : 0
        finalBoxQuantity = scanType === "OPEN" ? 0 : 1
      }

      const resp = await scanAuditLotMutation({
        variables: {
          scanLotsDto: {
            force: force,
            productId: product.id as string,
            scannedLotActionId: barcode,
            openQuantity: finalOpenQuantity,
            boxQuantity: finalBoxQuantity,
            auditId: auditId as string
          }
        }
      })

      if (resp.data?.scanAuditLot) {
        toast.success(`Lot ${barcode} scanned successfully`)
        setQuantityDialogOpen(false)
        setBatchQuantityDialogOpen(false)
        setScannedLotNumber("")
      }
    } catch (error) {
      console.log("error in handleOnScan", error)
      const errorMessage = (error as ApolloError)?.message || ""
      const errorMessageParts = errorMessage.split("~")
      const displayMessage = errorMessageParts[1] ?? errorMessageParts[0] ?? ""

      // Check if error message includes "Please continue with verification"
      if (displayMessage.includes("Please continue with verification")) {
        setErrorObject({
          message: displayMessage,
          code: "CONFLICT",
          variables: {
            quantity: quantity,
            barcode: barcode,
            scanType: scanType,
            openQuantity: openQuantity,
            boxQuantity: boxQuantity
          }
        })
      } else {
        toast.error(errorMessage)
      }
    }
  }
  const handleQuantityConfirm = async (quantity: number) => {
    await handleOnScan({ barcode: scannedLotNumber, quantity: quantity, scanType: "OPEN" })
  }

  const handleBatchQuantityConfirm = async (openQuantity: number, boxQuantity: number) => {
    await handleOnScan({
      barcode: scannedLotNumber,
      openQuantity: openQuantity,
      boxQuantity: boxQuantity
    })
  }

  const handleQuantityDialogClose = () => {
    setQuantityDialogOpen(false)
    setScannedLotNumber("")
  }

  const handleBatchQuantityDialogClose = () => {
    setBatchQuantityDialogOpen(false)
    setScannedLotNumber("")
  }

  if (isLoadingAuditProductSummary || isRefetching || isScanning) return <LotScanningSkeleton />
  return (
    <Box>
      {/* <PageLoader isLoading={isScanning} /> */}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Inventory color="primary" />
          <Box>
            <Typography variant="h6">Scanning Lots for {product.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Lot Count: {product.lotCount}
            </Typography>
          </Box>
        </Box>
        <Switch
          checked={isBatchMode}
          onChange={(val) => setIsBatchMode(val ?? false)}
          title="SKU/Batch Mode"
          subtitle={isBatchMode ? "On" : "Off"}
        />
      </Box>
      {/* Barcode Scanner */}
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2} mb={2}>
        <BarcodeScanner
          label="Lot Number"
          onScan={(barcode) => {
            console.log("barcode....", barcode)
            handleAddLot(barcode)
          }}
          placeholder="Scan or enter lot number"
          fullWidth
          sx={{ height: "35px" }}
        />
        {!isBatchMode && (
          <Stack variant="rowCenter" spacing={2}>
            <Typography variant="body1">Scan Type:</Typography>

            <MinimulTabs
              handleTabChange={setCardViewTab}
              gap={"0px"}
              sx={{ width: "50%", height: "40px" }}
              tabs={[
                {
                  label: "Open",
                  value: "OPEN",
                  icon: <Inventory2 sx={{ fontSize: "16px" }} /> // Suggestion: Inventory2 for "Open"
                },
                {
                  label: "Box",
                  value: "BOX",
                  icon: <MoveToInbox sx={{ fontSize: "16px" }} /> // Suggestion: MoveToInbox for "Box"
                }
              ]}
              value={cardViewTab}
            />
          </Stack>
        )}
      </Box>
      <Alert severity="info">Start scanning lots by entering lot numbers and quantities above</Alert>
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Tooltip title="Sync">
          <IconButton onClick={() => refetchAuditProductSummary()}>
            <CloudSyncIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {/* Quick Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom color="primary.main">
            Quick Overview
          </Typography>
          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {getDisplayPrice(auditProductSummary?.getAuditProductSummary?.stockValue ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stock Value
                </Typography>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {auditProductSummary?.getAuditProductSummary?.totalLots}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Lots
                </Typography>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {auditProductSummary?.getAuditProductSummary?.scannedLots}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Scanned Lots
                </Typography>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {auditProductSummary?.getAuditProductSummary?.remainingLots}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Remaining Lots
                </Typography>
              </Box>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Button variant="contained" color="primary" onClick={onContinue}>
          Continue
        </Button>
      </Stack>
      {/* Quantity Input Dialog */}
      <QuantityInputDialog
        open={quantityDialogOpen}
        onClose={handleQuantityDialogClose}
        onConfirm={handleQuantityConfirm}
        lotNumber={scannedLotNumber}
      />
      {/* Batch Quantity Input Dialog */}
      <BatchQuantityDialog
        open={batchQuantityDialogOpen}
        onClose={handleBatchQuantityDialogClose}
        onConfirm={handleBatchQuantityConfirm}
        lotNumber={scannedLotNumber}
        loading={isScanning}
      />
      <ForceAuditDialog
        open={errorObject?.code === "CONFLICT"}
        toggleDialog={() => setErrorObject(null)}
        onConfirm={() => {
          const errorVars = errorObject?.variables
          setErrorObject(null)

          if (isBatchMode && errorVars?.openQuantity !== undefined && errorVars?.boxQuantity !== undefined) {
            // Batch mode: use openQuantity and boxQuantity
            handleOnScan({
              barcode: errorVars.barcode ?? scannedLotNumber,
              openQuantity: errorVars.openQuantity,
              boxQuantity: errorVars.boxQuantity,
              force: true
            })
          } else {
            // Regular mode: use scanType and quantity
            const scanType = errorVars?.scanType ?? cardViewTab
            handleOnScan({
              barcode: errorVars?.barcode ?? scannedLotNumber,
              quantity: errorVars?.quantity,
              scanType: scanType,
              force: true
            })
          }
        }}
        message={errorObject?.message ?? ""}
      />
    </Box>
  )
}

export default LotScanning

const ForceAuditDialog = ({
  open,
  toggleDialog,
  message,
  onConfirm
}: {
  open: boolean
  toggleDialog: () => void
  message: string
  onConfirm: () => void
}) => {
  return (
    <Dialog
      sx={{ "& .MuiPaper-root": { borderRadius: 3 } }}
      aria-hidden={false}
      fullWidth
      maxWidth="sm"
      open={open}
      // onClose={toggleDialog}
    >
      <DialogTitle>Quantity Mismatch</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {message && <Alert severity="error">{message}</Alert>}
          <Typography variant="body1">Are you sure you want to Proceed?</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={onConfirm}>
          Proceed
        </Button>
        <Button variant="contained" color="primary" onClick={toggleDialog}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
